import * as React from "react"
import { MapPin, ExternalLink, Navigation } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"

export interface LocationMessageProps
  extends React.HTMLAttributes<HTMLDivElement> {
  title: string
  address: string
  latitude?: number
  longitude?: number
  showMapPreview?: boolean
  onMapClick?: () => void
  onNavigate?: (lat: number, lng: number) => void
  mapProvider?: "google" | "apple" | "openstreetmap"
}

const LocationMessageInner = React.forwardRef<HTMLDivElement, LocationMessageProps>(
  (
    {
      className,
      title,
      address,
      latitude,
      longitude,
      showMapPreview = true,
      onMapClick,
      onNavigate,
      mapProvider = "google",
      ...props
    },
    ref
  ) => {
    // Check if we have valid coordinates
    const hasCoordinates = latitude !== undefined && longitude !== undefined

    const getMapUrl = React.useCallback(
      (provider: "google" | "apple" | "openstreetmap") => {
        // Use coordinates if available, otherwise use address string
        if (hasCoordinates) {
          switch (provider) {
            case "google":
              return `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`
            case "apple":
              return `https://maps.apple.com/?q=${latitude},${longitude}`
            case "openstreetmap":
              return `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}#map=15/${latitude}/${longitude}`
            default:
              return `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`
          }
        } else {
          // Fallback to address-based search
          const encodedAddress = encodeURIComponent(address)
          switch (provider) {
            case "google":
              return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`
            case "apple":
              return `https://maps.apple.com/?q=${encodedAddress}`
            case "openstreetmap":
              return `https://www.openstreetmap.org/search?query=${encodedAddress}`
            default:
              return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`
          }
        }
      },
      [latitude, longitude, address, hasCoordinates]
    )

    const getStaticMapUrl = React.useCallback(() => {
      // Only show embedded map if we have coordinates
      if (!hasCoordinates) {
        return null
      }
      // Using OpenStreetMap tiles via staticmap service
      return `https://www.openstreetmap.org/export/embed.html?bbox=${longitude! - 0.01},${latitude! - 0.01},${longitude! + 0.01},${latitude! + 0.01}&layer=mapnik&marker=${latitude},${longitude}`
    }, [latitude, longitude, hasCoordinates])

    const handleMapClick = React.useCallback(() => {
      if (onMapClick) {
        onMapClick()
      } else {
        window.open(getMapUrl(mapProvider), "_blank", "noopener,noreferrer")
      }
    }, [onMapClick, getMapUrl, mapProvider])

    const handleNavigate = React.useCallback(() => {
      if (onNavigate && hasCoordinates) {
        onNavigate(latitude!, longitude!)
      } else {
        window.open(getMapUrl("google"), "_blank", "noopener,noreferrer")
      }
    }, [onNavigate, latitude, longitude, getMapUrl, hasCoordinates])

    return (
      <div
        ref={ref}
        className={cn(
          "rounded-2xl border-2 border-white/80 bg-white/70 backdrop-blur-sm overflow-hidden shadow-lg shadow-cyan-100/30 max-w-sm",
          className
        )}
        role="article"
        aria-label={`Location: ${title}`}
        {...props}
      >
        {/* Map Preview */}
        {showMapPreview && (
          <div
            className="relative w-full h-40 bg-gray-100 cursor-pointer group overflow-hidden"
            onClick={handleMapClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                handleMapClick()
              }
            }}
            aria-label="View location on map"
          >
            {/* Map iframe or placeholder */}
            {hasCoordinates && getStaticMapUrl() ? (
              <iframe
                src={getStaticMapUrl()!}
                className="w-full h-full pointer-events-none"
                title={`Map showing ${title}`}
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                <MapPin className="size-8 text-cyan-500 mb-2" />
                <p className="text-sm text-gray-500 text-center px-4">
                  Click to view on map
                </p>
              </div>
            )}

            {/* Overlay on hover */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/95 backdrop-blur-sm rounded-full p-3 shadow-lg">
                <ExternalLink className="size-5 text-cyan-600" />
              </div>
            </div>
          </div>
        )}

        {/* Location Details */}
        <div className="p-4 space-y-3 bg-gradient-to-b from-white/60 to-white/90">
          {/* Title with Icon */}
          <div className="flex items-start gap-2">
            <MapPin className="size-5 text-cyan-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm text-gray-800 line-clamp-2">
                {title}
              </h3>
            </div>
          </div>

          {/* Address */}
          <p className="text-xs text-gray-600 leading-relaxed pl-7">
            {address}
          </p>

          {/* Coordinates (only show if available) */}
          {hasCoordinates && (
            <p className="text-xs text-gray-400 font-mono pl-7">
              {latitude!.toFixed(6)}, {longitude!.toFixed(6)}
            </p>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 pt-1">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 bg-white/80 hover:bg-white border-2 border-white/90 shadow-md hover:shadow-lg text-gray-700 transition-all hover:scale-105"
              onClick={handleMapClick}
              aria-label="Open in maps"
            >
              <ExternalLink className="size-3.5 mr-1.5" />
              View Map
            </Button>
            <Button
              variant="default"
              size="sm"
              className="flex-1 bg-gradient-to-br from-gray-700 to-gray-900 hover:from-gray-800 hover:to-black text-white border-2 border-white/60 shadow-lg hover:shadow-xl transition-all hover:scale-105"
              onClick={handleNavigate}
              aria-label="Get directions"
            >
              <Navigation className="size-3.5 mr-1.5" />
              Navigate
            </Button>
          </div>
        </div>
      </div>
    )
  }
)

LocationMessageInner.displayName = "LocationMessageInner"

// Memoize to prevent re-renders when parent re-renders
const LocationMessage = React.memo(LocationMessageInner)
LocationMessage.displayName = "LocationMessage"

export { LocationMessage }
